import { lazy, Suspense, useState } from 'react'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Modal, Tag, Typography } from 'antd'
import { DEFAULT_RESOURCE_SEARCH as DEFAULT_SEARCH } from '@/const/list.ts'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { cn } from '@/utils/index.ts'
import { RentalResourceSelectionList } from './rental-resource-selection-list.tsx'

const SiteQuickCreateModal = lazy(() =>
  import('@/features/master-data/index.ts').then((module) => ({ default: module.SiteQuickCreateModal }))
)

export type RentalSiteRow = {
  id: number
  name: string
  provinceName: string
  cityName: string
  districtName: string | null
  detailAddress: string
  organization: { id: number; name: string; isDisable: boolean } | null
  isDisable: boolean
}

export type RentalRoomRow = {
  id: number
  siteId: number
  roomNo: string
  floor: string | null
  roomType: 'office' | 'workstation' | 'warehouse' | 'other'
  area: string | null
  status: 'vacant' | 'reserved' | 'occupied' | 'maintenance' | 'disabled'
  site: { id: number; name: string; organization: { id: number; name: string } | null }
}

type ResourceSearch = {
  current: number
  pageSize: number
  keyword?: string
}

function formatSiteAddress(site: RentalSiteRow) {
  return [site.provinceName, site.cityName, site.districtName, site.detailAddress].filter(Boolean).join(' ')
}

function SitePanel({
  defaultPage = 1,
  open,
  selected,
  onSelect
}: {
  defaultPage?: number
  open: boolean
  selected?: RentalSiteRow
  onSelect: (site: RentalSiteRow) => void
}) {
  const trpc = useTRPC()
  const [search, setSearch] = useState<ResourceSearch>(DEFAULT_SEARCH)
  const [keyword, setKeyword] = useState('')
  const current = search.keyword ? search.current : Math.max(search.current, defaultPage)
  const pages = useQueries({
    queries: Array.from({ length: current }, (_, index) => ({
      ...trpc.site.list.queryOptions({ ...search, current: index + 1, status: 'enabled' }),
      staleTime: 1_000,
      enabled: open
    }))
  })
  const items = [...new Map(pages.flatMap((page) => page.data?.data ?? []).map((item) => [item.id, item])).values()]
  const loading = pages.some((page) => page.isFetching)
  const failedPage = pages.find((page) => page.isError)
  const total = pages[0]?.data?.total ?? 0
  const hasMore = (pages.at(-1)?.data?.data.length ?? 0) > 0 && current * search.pageSize < total

  return (
    <RentalResourceSelectionList
      emptyText="暂无可用地址"
      error={failedPage ? getErrorMessage(failedPage.error, '地址加载失败，请重试') : undefined}
      getLabel={(site) => site.name}
      items={items}
      keyword={keyword}
      loading={loading}
      onKeywordChange={setKeyword}
      onRetry={() => void failedPage?.refetch()}
      onSearch={() => setSearch((previous) => ({ ...previous, current: 1, keyword: keyword.trim() || undefined }))}
      onSelect={onSelect}
      hasMore={hasMore}
      onLoadMore={() => {
        if (open && hasMore && !loading && !failedPage) {
          setSearch((previous) => ({ ...previous, current: current + 1 }))
        }
      }}
      resetKey={search.keyword ?? ''}
      total={total}
      renderPrimary={(site) => (
        <span className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 whitespace-normal">
          <span className="min-w-0 break-words">{site.name}</span>
          <span
            className="flex shrink-0 items-center gap-2"
            aria-label={`房间总数 ${site.roomCount} 间，可用 ${site.availableRoomCount} 间`}
          >
            <span className="text-content-muted text-xs font-normal">
              共 <span className="tabular-nums">{site.roomCount}</span> 间
            </span>
            <Tag className="m-0! px-2! py-0.5! font-normal" color={site.availableRoomCount > 0 ? 'success' : 'default'}>
              {site.availableRoomCount > 0 ? (
                <>
                  可用 <span className="tabular-nums">{site.availableRoomCount}</span> 间
                </>
              ) : (
                '暂无可用'
              )}
            </Tag>
          </span>
        </span>
      )}
      renderSecondary={(site) => (
        <>
          <span className="block">{formatSiteAddress(site) || '未填写详细地址'}</span>
          <span className="block">{site.organization?.name ?? '未绑定经营主体'}</span>
        </>
      )}
      searchLabel="搜索地址"
      searchPlaceholder="名称或地址"
      selectedId={selected?.id}
      title="地址"
    />
  )
}

function RoomPanel({
  open,
  siteId,
  onClearRoom,
  selected,
  onSelect
}: {
  open: boolean
  siteId: number
  onClearRoom?: () => void
  selected?: RentalRoomRow
  onSelect: (room: RentalRoomRow) => void
}) {
  const trpc = useTRPC()
  const [search, setSearch] = useState<ResourceSearch>(DEFAULT_SEARCH)
  const [keyword, setKeyword] = useState('')
  const pages = useQueries({
    queries: Array.from({ length: search.current }, (_, index) => ({
      ...trpc.room.list.queryOptions({
        ...search,
        current: index + 1,
        siteId,
        status: 'vacant',
        sortField: 'roomNo',
        sortOrder: 'asc'
      }),
      enabled: open
    }))
  })
  const items = [...new Map(pages.flatMap((page) => page.data?.data ?? []).map((item) => [item.id, item])).values()]
  const loading = pages.some((page) => page.isFetching)
  const failedPage = pages.find((page) => page.isError)
  const total = pages[0]?.data?.total ?? 0
  const hasMore = (pages.at(-1)?.data?.data.length ?? 0) > 0 && search.current * search.pageSize < total

  return (
    <RentalResourceSelectionList
      emptyText="该地址暂无空闲房间"
      error={failedPage ? getErrorMessage(failedPage.error, '房间加载失败，请重试') : undefined}
      getLabel={(room) => room.roomNo}
      items={items}
      keyword={keyword}
      loading={loading}
      onKeywordChange={setKeyword}
      onRetry={() => void failedPage?.refetch()}
      onSearch={() => setSearch((previous) => ({ ...previous, current: 1, keyword: keyword.trim() || undefined }))}
      onSelect={onSelect}
      hasMore={hasMore}
      onLoadMore={() => {
        if (open && hasMore && !loading && !failedPage) {
          setSearch((previous) => ({ ...previous, current: previous.current + 1 }))
        }
      }}
      resetKey={search.keyword ?? ''}
      total={total}
      renderPrimary={(room) => room.roomNo}
      renderSecondary={(room) =>
        [room.floor ? `${room.floor} 层` : '未填楼层', room.area ? `${room.area} ㎡` : '未填面积', '空闲'].join(' · ')
      }
      searchLabel="搜索房间"
      searchPlaceholder="房间号或楼层"
      selectedId={selected?.id}
      title="房间"
      titleExtra={
        onClearRoom ? (
          <Button onClick={onClearRoom} size="small" type="text">
            仅选地址
          </Button>
        ) : undefined
      }
    />
  )
}

type SelectionProps = {
  siteOnly?: boolean
  selectedSite?: RentalSiteRow
  selectedRoom?: RentalRoomRow
  businessId?: number
  requireRoom: boolean
  onCancel: () => void
  onSelect: (site: RentalSiteRow, room?: RentalRoomRow) => void
}

export function RentalResourceSelectorModal({
  selectedSite,
  selectedRoom,
  businessId,
  siteOnly = false,
  requireRoom,
  onCancel,
  onSelect
}: SelectionProps) {
  const trpc = useTRPC()
  const [open, setOpen] = useState(true)
  const [creating, setCreating] = useState(false)
  const [sitePanelVersion, setSitePanelVersion] = useState(0)
  const [manualSite, setSite] = useState(selectedSite)
  const queryClient = useQueryClient()
  const defaultSite = useQuery({
    queryKey: [...trpc.site.list.queryOptions({ ...DEFAULT_SEARCH, status: 'enabled' }).queryKey, 'first-available'],
    queryFn: async ({ signal }) => {
      for (let current = 1; !signal.aborted; current += 1) {
        const page = await queryClient.fetchQuery(
          trpc.site.list.queryOptions({ ...DEFAULT_SEARCH, current, status: 'enabled' })
        )
        for (const candidate of page.data) {
          if (candidate.availableRoomCount > 0) {
            return { site: candidate, current }
          }
        }
        if (page.data.length === 0 || current * DEFAULT_SEARCH.pageSize >= page.total) {
          return { site: undefined, current }
        }
      }
      return null
    },
    enabled: open && !manualSite,
    staleTime: 0,
    refetchOnWindowFocus: false
  })
  const site = manualSite ?? defaultSite.data?.site
  // undefined uses the recommendation; null is an explicit site-only selection.
  const [manualRoom, setManualRoom] = useState<RentalRoomRow | null | undefined>(
    selectedRoom ?? (selectedSite && !requireRoom ? null : undefined)
  )
  const recommendation = useQuery({
    ...trpc.room.recommend.queryOptions({ siteId: site?.id ?? 0, businessId }),
    enabled: open && Boolean(site) && !siteOnly,
    staleTime: 0,
    refetchOnWindowFocus: false
  })
  const room = manualRoom !== undefined ? manualRoom : recommendation.data?.room
  const validRoom = room && room.siteId === site?.id ? room : undefined
  let roomSelectionLabel = ''
  if (validRoom && !siteOnly) {
    roomSelectionLabel = ` / ${validRoom.roomNo}`
  } else if (requireRoom) {
    roomSelectionLabel = ' / 未选择房间'
  }

  return (
    <Modal
      classNames={{ container: 'h-[80vh]', body: 'flex min-h-0 flex-col' }}
      destroyOnHidden
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span aria-live="polite" className="min-w-0 flex-1 text-left text-sm break-words">
            已选：{site?.name ?? '未选择地址'}
            {roomSelectionLabel}
          </span>
          <div className="ml-auto flex shrink-0 gap-2">
            <Button onClick={() => setOpen(false)}>取消</Button>
            <Button
              disabled={
                !site ||
                (requireRoom && !validRoom) ||
                (requireRoom && manualRoom === undefined && recommendation.isFetching)
              }
              onClick={() => {
                if (!site) {
                  return
                }
                onSelect(site, siteOnly ? undefined : validRoom)
                setOpen(false)
              }}
              type="primary"
            >
              确定选择
            </Button>
          </div>
        </div>
      }
      mask={{ closable: false }}
      onCancel={() => setOpen(false)}
      afterClose={onCancel}
      open={open}
      title={siteOnly ? '选择租赁地址' : '选择租赁场地与房间'}
      width="64rem"
    >
      <div className="mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
        <Typography.Text type="secondary">
          {siteOnly ? '选择一个地址，创建时自动分配不同的空闲房间。' : '选择一个地址和一个房间，只能单选。'}
        </Typography.Text>
        <Authorized permissions={['order.rental.create']}>
          <Button onClick={() => setCreating(true)}>快速创建地址</Button>
        </Authorized>
      </div>
      {creating && (
        <Suspense fallback={null}>
          <SiteQuickCreateModal
            onClose={() => setCreating(false)}
            onCreated={(createdSite) => {
              setSite(createdSite)
              setManualRoom(undefined)
              setSitePanelVersion((version) => version + 1)
            }}
          />
        </Suspense>
      )}
      <div
        className={cn(
          'grid min-h-0 flex-1 grid-cols-1 items-stretch gap-4',
          !siteOnly && 'grid-rows-2 md:grid-cols-2 md:grid-rows-1'
        )}
      >
        <SitePanel
          key={sitePanelVersion}
          defaultPage={defaultSite.data?.current}
          open={open}
          selected={site}
          onSelect={(next) => {
            if (next.id === site?.id) {
              return
            }
            setSite(next)
            setManualRoom(undefined)
          }}
        />
        {!siteOnly &&
          (site ? (
            <RoomPanel
              key={site.id}
              open={open}
              selected={validRoom}
              siteId={site.id}
              onSelect={setManualRoom}
              onClearRoom={!requireRoom ? () => setManualRoom(null) : undefined}
            />
          ) : (
            <section
              aria-label="房间列表"
              className="border-border bg-surface flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border"
            >
              <div className="border-border shrink-0 border-b px-4 py-3">
                <h3 className="text-content m-0 text-sm font-semibold">房间</h3>
              </div>
              <div className="text-content-soft flex min-h-0 flex-1 items-center justify-center p-6 text-sm">
                请先选择地址
              </div>
            </section>
          ))}
      </div>
    </Modal>
  )
}
